/**
 * Expired sessions (QA 2026-10-08): when a session ends without the player signing out (the refresh
 * token was revoked or expired, or the API refused the access token), the sign-in panel says "Your
 * session expired, sign in again" instead of silently showing the signed-out page.
 *
 * - `sessionExpired` is the flag the sign-in panel reads; it clears on the next sign-in.
 * - {@link endedBySessionExpiry} decides from an auth state change; {@link expiredAtStartup} from the
 *   stored session read when the app opens.
 * - `api.ts` reports a 401 to an authenticated call through {@link reportRejectedSession}; the
 *   `AuthProvider` then drops the local session and sets the flag.
 */
import { create } from 'zustand';

/** The flag and its setters. */
export interface SessionExpiryState {
  expired: boolean;
  markExpired: () => void;
  clear: () => void;
}

/** Whether the last session ended by expiring (not by signing out). */
export const useSessionExpiry = create<SessionExpiryState>((set) => ({
  expired: false,
  markExpired: () => {
    set({ expired: true });
  },
  clear: () => {
    set({ expired: false });
  },
}));

/** The copy shown on the sign-in panel. */
export const SESSION_EXPIRED_MESSAGE = 'Your session expired, sign in again.';

/**
 * Whether an auth state change ended a session by expiry: a `SIGNED_OUT` while signed in that the player
 * did not ask for (`userSignedOut`: the app's Sign out button was used).
 */
export function endedBySessionExpiry(event: string, hadSession: boolean, userSignedOut: boolean): boolean {
  return event === 'SIGNED_OUT' && hadSession && !userSignedOut;
}

/**
 * Whether the app opened with a stored session that could not be restored: the storage held a session
 * (`stored`, the raw value read before the client touched it) but the client came back without one
 * (its refresh was refused and the client dropped it). A refresh that only failed for the network keeps
 * the stored session, so it is not reported.
 */
export function expiredAtStartup(stored: string | null, restored: boolean): boolean {
  if (stored === null || restored) return false;
  return stored.includes('refresh_token');
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribes to API calls refused for the session (returns the unsubscribe). */
export function onRejectedSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reports that the server refused the access token of an authenticated call (HTTP 401). */
export function reportRejectedSession(): void {
  for (const l of listeners) l();
}
